import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle2, Copy, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentInstruction = () => {
  const { bookingCode } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReservation();
  }, [bookingCode]);

  const fetchReservation = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/reservations/check?booking_code=${bookingCode}`);
      if (response.data && response.data.length > 0) {
        setReservation(response.data[0]);
      } else {
        toast.error("Reservation not found");
        navigate('/');
      }
    } catch (error) {
      toast.error("Error fetching reservation details");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-emerald-50/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!reservation) return null;

  return (
    <div className="pt-20 min-h-screen bg-emerald-50/30 pb-12">
      <div className="max-w-3xl mx-auto px-4 mt-8">
        
        <button 
          onClick={() => navigate('/')} 
          className="flex items-center text-emerald-600 font-medium mb-6 hover:text-emerald-700 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </button>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-soft overflow-hidden"
        >
          {/* Header */}
          <div className="bg-emerald-600 px-6 py-8 text-center">
            {reservation.status === 'confirmed' ? (
              <div className="flex flex-col items-center">
                <CheckCircle2 className="w-16 h-16 text-white mb-4" />
                <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
                <p className="text-emerald-100">Your reservation is confirmed.</p>
              </div>
            ) : reservation.status === 'cancelled' ? (
              <div className="flex flex-col items-center">
                <AlertCircle className="w-16 h-16 text-red-200 mb-4" />
                <h1 className="text-2xl font-bold text-white mb-2">Payment Expired</h1>
                <p className="text-emerald-100">This payment session has expired.</p>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <RefreshCw className="w-8 h-8 text-white animate-spin-slow" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Waiting for Payment</h1>
                <p className="text-emerald-100">Please complete your payment before the timer expires.</p>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="p-6 sm:p-8">
            <div className="flex justify-between items-center mb-8 pb-6 border-b border-gray-100">
              <div>
                <p className="text-gray-500 text-sm mb-1">Booking Code</p>
                <p className="font-bold text-xl text-gray-900">{reservation.booking_code}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-500 text-sm mb-1">Total Amount</p>
                <p className="font-bold text-2xl text-emerald-600">
                  Rp {reservation.total_amount?.toLocaleString('id-ID')}
                </p>
              </div>
            </div>

            {reservation.status === 'pending' && reservation.ipaymu_payment_no && (
              <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4 text-center">Payment Details</h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Payment Method</p>
                      <p className="font-medium text-gray-900">{reservation.ipaymu_payment_name || reservation.payment_channel}</p>
                    </div>
                  </div>

                  {reservation.ipaymu_qr_image && reservation.payment_method === 'qris' ? (
                    <div className="flex flex-col items-center justify-center bg-white p-6 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 mb-4">Scan QRIS Code to Pay</p>
                      <img src={reservation.ipaymu_qr_image} alt="QRIS" className="w-48 h-48 object-contain mb-4" />
                      <p className="text-xs text-gray-400">Powered by iPaymu</p>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Payment Code / VA</p>
                        <p className="font-bold text-xl text-emerald-700 tracking-wider">
                          {reservation.ipaymu_payment_no}
                        </p>
                      </div>
                      <button 
                        onClick={() => copyToClipboard(reservation.ipaymu_payment_no)}
                        className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  {reservation.ipaymu_expired && (
                    <div className="text-center text-sm text-gray-500 mt-4">
                      Complete payment before: <span className="font-medium text-red-500">{reservation.ipaymu_expired}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3 text-sm text-gray-600">
              <h4 className="font-medium text-gray-900">Instructions:</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Make sure the transfer amount exactly matches the Total Amount above.</li>
                <li>Your reservation will be automatically confirmed after successful payment.</li>
                <li>Save your Booking Code to check your reservation status later.</li>
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100 flex justify-center">
              <button 
                onClick={fetchReservation}
                className="flex items-center text-emerald-600 font-medium hover:text-emerald-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Check Status Again
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentInstruction;
